import TextInput from "../../UI/TextInput";
import RadioGroup from "../../UI/RadioGroup";
import Radio from "../../UI/Radio";
import Tooltip from "../../UI/Tooltip";
import { IconInfoCircle } from "@tabler/icons-react";
import Checkbox from "../../UI/Checkbox";
import { ProfileStatus } from "../../../models/profile";
import Callout from "../../UI/Callout";
import styles from "./TenantConvertForm.module.css";
import Select, { ReactSelect } from "../../UI/Select";
import SelectIban from "../../UI/SelectIban";
import Text from "../../UI/Text";
import useLeaseTemplates from "../../../hooks/use-lease-templates";
import { DETAIL_CHARGES, DETAIL_CHARGES_PRO } from "../../../models/property";
import ExpandableCheckboxList from "../../UI/ExpandableCheckboxList";
import TextArea from "../../UI/TextArea";
import moment from "moment/moment";
import useIrls from "../../../hooks/use-irls";
import { useFormikContext } from "formik";
import FieldArrayAutreCharge from "../FieldArrayAutreCharge";
import {
  allowsChargesForfait,
  ChargesType,
  TenantLeaseType,
} from "../../../utils/constants/tenants";
import { TVA_RATES } from "../../../utils/constants/compta";

const TYPE_ANNEE = "année";
const TYPE_TRIMESTRE = "trimestre";

function GeneratePaymentsCheckbox({ className, disabled = false }) {
  return (
    <Checkbox name={"loyer_a_jour"} disabled={disabled} className={className}>
      Générer l'historique de paiement des loyers{" "}
      {!disabled && (
        <Tooltip
          content={
            <>
              Qalimo génère par défaut tout l'historique d'appels de loyer.
              Cocher cette case permet également de générer l'intégralité des
              paiements du locataire, comme s'il était à jour dans ses
              paiements. Vous pouvez modifier ces informations par la suite.
            </>
          }
        >
          <IconInfoCircle size={16} />
        </Tooltip>
      )}
    </Checkbox>
  );
}

export default function TenantConvertForm({
  propertyType,
  propertyIndice,
  propertyLoyerReference,
  propertyLoyerRefMaj,
  propertySurface,
  tenant,
  onLeaseChange,
  isRegenerate,
}) {
  const { irls } = useIrls();
  const { leaseTemplates, leaseTemplatesById, isLoading, error } =
    useLeaseTemplates({ withById: true });

  const { values, touched, setFieldValue, handleChange } = useFormikContext();

  if (isLoading || error) return null;

  const leaseTemplate = leaseTemplatesById[values.id_lease_template] || {};

  let detail_charges = [...DETAIL_CHARGES];
  if (leaseTemplates.habitation) {
    detail_charges = [...DETAIL_CHARGES, ...DETAIL_CHARGES_PRO];
  }

  const detailChargesCheckboxItems = [
    ...detail_charges,
    ...values.detail_charges.filter((i) => !detail_charges.includes(i)),
  ];

  function addItem(fieldName, item) {
    if (values[fieldName] === undefined || values[fieldName] === null) {
      setFieldValue(fieldName, [item]);
    } else if (!values[fieldName].includes(item)) {
      setFieldValue(fieldName, [...values[fieldName], item]);
    }
  }

  function updateIndice(e, type) {
    handleChange(e);
    const irl = irls.find(
      (irl) =>
        irl.annee ===
          parseInt(type === TYPE_ANNEE ? e.target.value : values.irl_annee) &&
        irl.trimestre ===
          parseInt(
            type === TYPE_TRIMESTRE ? e.target.value : values.irl_trimestre,
          ) &&
        irl.region === propertyIndice,
    );
    if (irl) {
      setFieldValue("irl_indice", irl.indice);
    } else {
      setFieldValue("irl_indice", "");
    }
  }

  const leaseTemplateOptions = leaseTemplates.map((option) => ({
    value: option.id,
    label: option.nom,
  }));

  return (
    <div className={"marginT10"}>
      {tenant?.profiles
        .filter((p) => p.status === ProfileStatus.Actif.status)
        .map((prof, index) => (
          <p key={index}>
            {prof.prenom} {prof.nom}
          </p>
        ))}
      <ReactSelect
        name="id_lease_template"
        label="Type de bail"
        mandatory
        className={"marginT10 marginB10"}
        dependon="id_bien"
        options={leaseTemplateOptions}
        onChange={onLeaseChange}
      />
      {values.genBail === "true" &&
        TenantLeaseType.isCommercial(leaseTemplate.type) && (
          <TextInput
            name={"activite_exerce"}
            label={"Activité exercée"}
            dependon={"id_bien"}
          />
        )}
      <div className={"gridColWrap240"}>
        <div>
          <TextInput
            type="number"
            name="loyer_hc"
            placeholder="380,10"
            step={"0.01"}
            dependon={"id_bien"}
            label={`Loyer hors charge ${
              values.zone_tendue === "true" ? " (hors complément de loyer)" : ""
            }`}
            unit={"€"}
            mandatory
          />
          <Checkbox
            name={"tva"}
            className={"marginB10"}
            dependon={"id_bien"}
            onChange={(e) => {
              handleChange(e);
              if (!e.target.checked) {
                setFieldValue("taux_tva", "");
              }
            }}
          >
            Le loyer est soumis à la TVA
          </Checkbox>
          {values.tva && (
            <ReactSelect
              name="taux_tva"
              label="Taux de TVA"
              mandatory
              options={TVA_RATES}
              className={"marginB10"}
              dependon={"id_bien"}
            />
          )}
        </div>
        <div>
          <TextInput
            type="number"
            name="charges"
            placeholder="50"
            step={"0.01"}
            unit={"€"}
            label="Charges"
            dependon={"id_bien"}
            mandatory
          />
          <Checkbox
            name={"tva_charge"}
            className={"marginB10"}
            dependon={"id_bien"}
            onChange={(e) => {
              handleChange(e);
              if (!e.target.checked) {
                setFieldValue("taux_tva_charge", "");
              }
            }}
          >
            Les charges sont soumises à la TVA
          </Checkbox>
          {values.tva_charge && (
            <ReactSelect
              name="taux_tva_charge"
              label="Taux de TVA sur les charges"
              mandatory
              options={TVA_RATES}
              className={"marginB10"}
              dependon={"id_bien"}
            />
          )}
        </div>
      </div>
      {propertyLoyerReference &&
        propertyLoyerRefMaj &&
        propertySurface &&
        values.loyer_hc >
          propertyLoyerRefMaj * propertySurface +
            (parseFloat(values.complement_loyer) || 0) && (
          <Callout variant="warning" className={styles.callout}>
            Le loyer hors charge ({values.loyer_hc} €) dépasse le maximum
            autorisé pour ce bien (
            {(
              propertyLoyerRefMaj * propertySurface +
              (parseFloat(values.complement_loyer) || 0)
            ).toFixed(2)}{" "}
            € soit {propertyLoyerRefMaj} €/m² × {propertySurface} m²
            {values.complement_loyer
              ? ` + ${values.complement_loyer} € de complément`
              : ""}
            ). En zone tendue, le loyer ne peut pas dépasser ce montant.
          </Callout>
        )}
      <FieldArrayAutreCharge dependon="id_bien" />
      {propertyLoyerReference && leaseTemplate.habitation && (
        <>
          <TextInput
            type={"number"}
            name={"complement_loyer"}
            label="Si besoin, complément de loyer prévu"
            placeholder={"100"}
            unit={"€"}
            dependon="id_bien"
            step={"0.01"}
          />
          <TextArea
            name={"justif_complement_loyer"}
            label="Justification du complément de loyer"
            placeholder={"Vue imprenable sur la ville"}
            dependon="id_bien"
            rows={4}
          />
        </>
      )}
      {leaseTemplate.type === TenantLeaseType.LCD && (
        <>
          <TextInput
            type={"number"}
            name="taxe_sejour"
            placeholder={"2,3"}
            unit="€"
            label="Montant taxe de séjour total"
            step={"0.01"}
            mandatory
          />
          <TextInput
            type={"number"}
            name="nombre_personne"
            placeholder={"2"}
            unit="personnes"
            label="Nombre de personne dans le logement"
            mandatory
          />
          <TextInput
            type={"number"}
            name="montant_surcharge"
            placeholder={"2,3"}
            unit="€"
            label={
              <>
                Montant par personne supplémentaire{" "}
                <Tooltip
                  content={
                    "Pénalités que vous pourrez appliquer par personne, si il y a plus que le nombre de personne indiqué dans le contrat"
                  }
                >
                  <IconInfoCircle size={16} />
                </Tooltip>
              </>
            }
            step={"0.01"}
            mandatory
          />
        </>
      )}
      {allowsChargesForfait(leaseTemplate) && (
        <RadioGroup>
          <Radio
            name="type_charge"
            value={ChargesType.PROVISION}
            label="Provision sur charge"
            dependon="id_bien"
          />
          <Radio
            name="type_charge"
            value={ChargesType.FORFAIT}
            label="Forfait de charge"
            dependon="id_bien"
          />
        </RadioGroup>
      )}
      Détail des charges facturées{" "}
      <Tooltip
        content={
          "Attention l’électricité n’est pas considérée comme une charge refacturable au locataire. " +
          "Vous ne pouvez donc théoriquement pas l’inclure dans le forfait ou la provision de charge."
        }
      >
        <IconInfoCircle size={16} />
      </Tooltip>{" "}
      :
      <ExpandableCheckboxList
        name="detail_charges"
        items={detailChargesCheckboxItems}
        addItem={addItem}
        className={"marginT10 marginB20"}
        dependon="id_bien"
      />
      {leaseTemplate.type !== TenantLeaseType.MOBILITE && (
        <>
          <TextInput
            type="number"
            name="deposit"
            placeholder="350"
            dependon="id_bien"
            label={"Montant du dépot de garantie"}
            mandatory
            unit={"€"}
            step={"0.01"}
          />
          {leaseTemplate.type === TenantLeaseType.MEUBLE &&
            values.deposit >
              ((parseFloat(values.loyer_hc) || 0) +
                (parseFloat(values.complement_loyer) || 0)) *
                2 && (
              <Callout variant="danger" className={styles.callout}>
                Dans le cadre d'un bail meublé, il est interdit d'avoir un dépôt
                de garantie supérieur à 2 mois de loyer hors charge
              </Callout>
            )}
          {leaseTemplate.type === TenantLeaseType.NU &&
            values.deposit >
              (parseFloat(values.loyer_hc) || 0) +
                (parseFloat(values.complement_loyer) || 0) && (
              <Callout variant="danger" className={styles.callout}>
                Dans le cadre d'un bail nu, il est interdit d'avoir un dépôt de
                garantie supérieur à 1 mois de loyer hors charge
              </Callout>
            )}
        </>
      )}
      {leaseTemplate.type !== TenantLeaseType.LCD && (
        <div className="flex wrap gap10 align-items-center">
          <TextInput
            type="number"
            name="day_payment"
            placeholder="05"
            dependon="id_bien"
            label={"Jour de paiement du loyer"}
            mandatory
            unit={"du mois"}
          />
          <Select name="payment_recurrence">
            <option value="1">Mensuel</option>
            <option value="3">Trimestriel</option>
            <option value="6">Semestriel</option>
            <option value="12">Annuel</option>
          </Select>
          {String(values.payment_recurrence) !== "1" && (
            <Checkbox name={"payment_periods_from_start_date"}>
              Périodes calées sur le mois de début du bail plutôt que sur
              l'année civile (ex. : bail trimestriel débutant le 12/12 : du
              12/12 au 28/02, puis du 01/03 au 31/05)
            </Checkbox>
          )}
        </div>
      )}
      <div className={"marginB10"}>
        <Text>
          IBAN{" "}
          <Tooltip
            content={
              "L'IBAN sera enregistré sur le bien, intégré au bail et envoyé par mail au locataire lors du premier contact. Veuillez vérifier que l'IBAN est correct."
            }
          >
            <IconInfoCircle size={16} />
          </Tooltip>
        </Text>
        <SelectIban dependon="id_bien" />
      </div>
      <ReactSelect
        name="raison_mobilite"
        hidden={leaseTemplate.type !== TenantLeaseType.MOBILITE}
        label={"Motif du bail mobilité"}
        mandatory
        className={"marginB10"}
        options={[
          {
            label: "En formation professionnelle",
            value: "en formation professionnelle",
          },
          {
            label: "En contrat d’apprentissage",
            value: "en contrat d’apprentissage",
          },
          {
            label:
              "En mission temporaire dans le cadre de son activité professionnelle",
            value:
              "en mission temporaire dans le cadre de son activité professionnelle",
          },
          {
            label: "En stage",
            value: "en stage",
          },
          {
            label:
              "En engagement volontaire dans le cadre de son service civique",
            value:
              "en engagement volontaire dans le cadre de son service civique",
          },
          {
            label: "En mutation professionnelle",
            value: "en mutation professionnelle",
          },
          {
            label: "En études supérieures",
            value: "en études supérieures",
          },
        ]}
      />
      {leaseTemplate.type === TenantLeaseType.LCD && (
        <>
          <div className={"marginB10 flex"}>
            <Radio
              name="acompte"
              value="false"
              label={"Paiement intégral du séjour"}
              className={"flex-grow"}
            />
            <Radio
              name="acompte"
              value="true"
              label={"Versement d'un acompte"}
              className={"flex-grow"}
            />
          </div>
          {values.acompte === "false" ? (
            <TextInput
              type={"date"}
              label={"Date limite de paiement du séjour"}
              name={"date_paiement_solde"}
              mandatory
            />
          ) : (
            <>
              <TextInput
                type={"number"}
                label={"Pourcentage de l’acompte"}
                name={"pourcentage_acompte"}
                unit={"%"}
              />
              <Select
                name={"type_acompte"}
                label={"Paiement du solde de la réservation"}
              >
                <option value={"jour_entree"}>
                  Le jour de son entrée dans les lieux et de la remise des clés
                </option>
                <option value={"date_limite"}>
                  Avant son entrée dans les lieux, et au plus tard à la date
                  définie ci-après
                </option>
                <option value={"jour_depart"}>
                  Le jour de son départ des lieux et de la restitution des clés
                </option>
                <option value={"condition_specifique"}>
                  Dans les conditions particulières ci-après définies
                </option>
              </Select>
              {values.type_acompte === "date_limite" && (
                <TextInput
                  type={"date"}
                  label={"Date limite de versement du solde de la réservation"}
                  name={"date_limite_paiement_solde"}
                />
              )}
              {values.type_acompte === "condition_specifique" && (
                <TextArea
                  label={"Conditions du versement du solde de la réservation"}
                  rows="4"
                  name={"condition_paiement_solde"}
                />
              )}
            </>
          )}
        </>
      )}
      {leaseTemplate.habitation && (
        <>
          <div className={"flex wrap gap10"}>
            <TextInput
              type="number"
              name="dernier_loyer"
              placeholder="370"
              label={
                <>
                  Dernier loyer versé{" "}
                  <Tooltip
                    direction={"top"}
                    content={
                      "Montant du dernier loyer hors charge versé par le locataire précédent. Cette information est obligatoire lorsque le dernier locataire a quitté le logement moins de dix-huit mois avant la signature du bail"
                    }
                  >
                    <IconInfoCircle size={16} />
                  </Tooltip>
                </>
              }
              unit={"€"}
              step={"0.01"}
            />
            <TextInput
              type="date"
              name="date_dernier_loyer"
              label={"Date du dernier loyer perçu"}
            />
          </div>
          <div className={"flex wrap gap10"}>
            <TextInput
              type="number"
              name="montant_dernier_travaux"
              placeholder="370"
              label={"Montant des derniers travaux réalisés"}
              unit={"€"}
              step={"0.01"}
            />
            <TextInput
              type="text"
              name="nature_dernier_travaux"
              label={"Description des derniers travaux réalisés"}
              placeholder={"Remplacement chauffage"}
              className={"flex-grow"}
            />
          </div>
        </>
      )}
      {values.genBail === "false" &&
        ![
          TenantLeaseType.LCD,
          TenantLeaseType.MOBILITE,
          TenantLeaseType.ETUDIANT,
        ].includes(leaseTemplate.type) && (
          <>
            <p>Indice de référence ({propertyIndice})</p>
            <div className={"flex gap10 wrap"}>
              <Select
                name={"irl_trimestre"}
                onChange={(e) => updateIndice(e, TYPE_TRIMESTRE)}
                className={"marginT0"}
              >
                <option value={"1"}>T1</option>
                <option value={"2"}>T2</option>
                <option value={"3"}>T3</option>
                <option value={"4"}>T4</option>
              </Select>
              <TextInput
                type="number"
                name="irl_annee"
                placeholder={"Année"}
                onChange={(e) => updateIndice(e, TYPE_ANNEE)}
              />
              <TextInput
                type="number"
                name="irl_indice"
                placeholder={"Indice"}
                step={"0.01"}
              />
            </div>
            {TenantLeaseType.isCommercial(leaseTemplate.type) && (
              <Checkbox name={"is_triennal"} className={"marginB10"}>
                Révision triennale
              </Checkbox>
            )}
          </>
        )}
      {(values.genBail === "false" ||
        propertyType === "commercial" ||
        leaseTemplate.type === TenantLeaseType.CIVIL ||
        leaseTemplate.type === TenantLeaseType.CIVIL_HABITATION ||
        leaseTemplate.type === TenantLeaseType.STOCKAGE ||
        leaseTemplate.type === TenantLeaseType.GARAGE) && (
        <>
          <TextInput
            type="number"
            name="duree_contrat"
            label={"Durée du contrat en mois"}
            placeholder={"36"}
            mandatory
            unit={"mois"}
          />
          {touched.duree_contrat &&
            ((leaseTemplate.type === TenantLeaseType.COMMERCIAL &&
              values.duree_contrat < 108) ||
              (leaseTemplate.type === TenantLeaseType.COMMERCIAL_DEROGATOIRE &&
                values.duree_contrat > 36) ||
              (leaseTemplate.type === TenantLeaseType.PROFESSIONNEL &&
                values.duree_contrat < 72)) && (
              <Callout variant="danger" className={styles.callout}>
                <ul>
                  <li>Bail commercial : durée MINIMALE de 9 ans (108 mois)</li>
                  <li>
                    bail professionnel : durée MINIMALE de 6 ans (72 mois)
                  </li>
                  <li>bail dérogatoire : durée MAXIMALE de 3 ans (36 mois)</li>
                </ul>
              </Callout>
            )}
        </>
      )}
      {leaseTemplate.type === TenantLeaseType.LCD && (
        <div className={"flex wrap gap10"}>
          <TextInput
            type="time"
            name="hour_start"
            label={"Heure de début du contrat"}
            mandatory
          />
          <TextInput
            type="time"
            name="hour_end"
            label={"Heure de fin du contrat"}
            mandatory
          />
        </div>
      )}
      {[TenantLeaseType.LCD, TenantLeaseType.MOBILITE].includes(
        leaseTemplate.type,
      ) && (
        <TextInput
          type="date"
          name="fin"
          label={"Date de fin du contrat"}
          mandatory
        />
      )}
      {values.genBail === "true" && (
        <>
          <TextInput
            type="date"
            name="debut"
            label={"Date de début du contrat"}
            mandatory
            className={"flex-grow"}
          />
          {leaseTemplate.type === TenantLeaseType.LCD &&
            moment(values.fin).diff(moment(values.debut), "days") > 90 && (
              <Callout variant="danger" className={styles.callout}>
                Un bail de type LCD ne peut exceder 90 jours.
              </Callout>
            )}
          {leaseTemplate.type === TenantLeaseType.MOBILITE &&
            touched.fin &&
            (moment(values.fin).diff(moment(values.debut), "months") < 1 ||
              moment(values.fin).diff(moment(values.debut), "months") >=
                10) && (
              <Callout variant="danger" className={styles.callout}>
                Un bail mobilité fait minimum 1 mois et maximum 10 mois. Il
                n'est ni renouvelable, ni reconductible. La date maximale de fin
                du bail est entre le{" "}
                {moment(values.debut).add(1, "months").format("DD/MM/YYYY")} et
                le {moment(values.debut).add(10, "months").format("DD/MM/YYYY")}
              </Callout>
            )}
          {(leaseTemplate.type === TenantLeaseType.CIVIL ||
            leaseTemplate.type === TenantLeaseType.CIVIL_HABITATION ||
            leaseTemplate.type === TenantLeaseType.STOCKAGE ||
            leaseTemplate.type === TenantLeaseType.GARAGE ||
            leaseTemplate.type === TenantLeaseType.PARKING) && (
            <>
              <div className={"flex wrap gap10 align-items-start"}>
                <TextInput
                  type="text"
                  name="preavis_locataire"
                  label={"Nombre de mois de préavis locataire"}
                  mandatory
                  className={"flex-grow"}
                  unit={"mois"}
                />
                <Select
                  className={""}
                  name={"echeance_locataire"}
                  label={"Echéance du préavis"}
                >
                  <option value={"date_aniversaire"}>
                    A date anniversaire
                  </option>
                  <option value={"anytime"}>A tout moment</option>
                </Select>
              </div>
              <div className={"flex wrap gap10 align-items-start"}>
                <TextInput
                  type="text"
                  name="preavis_bailleur"
                  label={"Nombre de mois de préavis bailleur"}
                  mandatory
                  className={"flex-grow"}
                  unit={"mois"}
                />
                <Select
                  name={"echeance_bailleur"}
                  label={"Echéance du préavis"}
                  className={""}
                >
                  <option value={"date_aniversaire"}>
                    A date anniversaire
                  </option>
                  <option value={"anytime"}>A tout moment</option>
                </Select>
              </div>
            </>
          )}
          {leaseTemplate.type === TenantLeaseType.CIVIL_HABITATION && (
            <Checkbox name={"is_meuble"} className={"marginB10"}>
              Il s'agit d'un logement meublé
            </Checkbox>
          )}
          {isRegenerate && (
            <>
              <Checkbox name={"generate_initial_balance"}>
                Réinitialiser les balances{" "}
                <Tooltip
                  content={
                    <>
                      Réinitialiser les balances supprimera tous les appels et
                      les paiements associés à ce locataire. Peut être utile si
                      vous avez changé le montant du loyer ou la date de début
                      du bail.
                    </>
                  }
                >
                  <IconInfoCircle size={16} />
                </Tooltip>
              </Checkbox>
              <GeneratePaymentsCheckbox
                disabled={!values.generate_initial_balance}
                className="marginL10"
              />
            </>
          )}
        </>
      )}
      {values.genBail === "false" && (
        <>
          <div className={"flex gap10 wrap"}>
            <TextInput
              type="date"
              name="debut"
              label={"Date de début du contrat"}
              mandatory
              className={"flex-grow"}
            />
            <TextInput
              type="date"
              name="date_signature"
              label={"Date de signature du bail"}
              mandatory
              className={"flex-grow"}
            />
          </div>
          {leaseTemplate.type !== TenantLeaseType.LCD && (
            <GeneratePaymentsCheckbox />
          )}
        </>
      )}
      <br />
    </div>
  );
}
